import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from '@mui/icons-material/Edit';
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect, useCallback } from "react";
import useAxiosRequests from "../../../hooks/useAxiosRequest";
import ExternalRoutes from "../../../config/ExternalRoutes";
import CompanyEdit from "./CompanyEdit";
import { useNavigate } from "react-router";
import ShowUser from "../users/ShowUser";

const RenderCellDocument = ({ row }) => {
  const documents = row.documents || [];
  const society = row;
  const navigate = useNavigate();
  const hanldeView = () => {
    if (documents.length > 0) {
      navigate('/contab-admin/document', { state: { society } })
    }
  }
  return (
    <Tooltip>
      <span className="kl-link-admin" onClick={hanldeView}>{documents.length}</span>
    </Tooltip>
  );
}


const RenderCellUser = ({ row }) => {
  const user = Array.isArray(row.users) ? row.users : [];
    const firstUser = user[0];
    const [showUser, setShowUser] = useState(false);

    const hanldeViewUser = () => {
      setShowUser(!showUser);
    }

  return (
    <Tooltip title={`${firstUser?.email || ""} | ${firstUser?.phone || ""}`}>
      <span className="kl-link-admin" onClick={hanldeViewUser}>{`${firstUser?.email || ""} | ${firstUser?.phone || ""}`}</span>
      <ShowUser open={showUser} handleClose={hanldeViewUser} userData={firstUser} />
    </Tooltip>
  );
}

const CompanyList = () => {
  const { sendRequest } = useAxiosRequests();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState({});

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await sendRequest({
        method: "GET",
        url: ExternalRoutes.society.list,
        config: {
          params: {
            order: {
              [sortModel.field]: sortModel.sort
            },
            page: paginationModel.page + 1,
            itemsPerPage: paginationModel.pageSize,
            query: searchQuery
          },
        }
      });

      if (response?.member) {
        setCompanies(response);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, sendRequest, sortModel.field, sortModel.sort]);

  console.log("companies", companies);
  useEffect(() => {
    fetchCompanies();
  }, [sortModel, paginationModel, searchQuery]);

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCompany(null);
  };

  const handleCompanyUpdated = () => {
    setLoading(true);
    setIsDrawerOpen(false);
    setSelectedCompany(null);
    fetchCompanies();
  };

  const handleSortModelChange = (newSortingModel) => {
    setLoading(true);
    if (newSortingModel.length > 0) {
      setSortModel(newSortingModel[0])
    }
  };

  const handlePaginationChange = (newPaginationModel) => {
    setLoading(true);
    setPaginationModel(newPaginationModel)
  };

  const handleEdit = (company) => {
    const userId = Array.isArray(company.users) && company.users[0]?.id ? company.users[0].id : null;
    setSelectedCompany({ ...company, user_id: userId });
    setIsDrawerOpen(true);
  };

  const columns = [
    {
      field: "sctName",
      headerName: "Nom de la société",
      flex: 0.15,
      sortable: true,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "sctAddress",
      headerName: "Adresse",
      flex: 0.15,
      sortable: true,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "sctSiret",
      headerName: "SIRET",
      flex: 0.15,
      sortable: true,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "users",
      headerName: "Utilisateur associé",
      flex: 0.15,
      sortable: false,
      minWidth: 120,
      renderCell: RenderCellUser
    },
    {
      field: "documents",
      headerName: "Documents",
      flex: 0.15,
      sortable: false,
      minWidth: 120,
      renderCell: RenderCellDocument
    },
    {
      field: "sctMyUnisoftId",
      headerName: "Id MyUnisoft",
      flex: 0.15,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value || ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.15,
      sortable: false,
      minWidth: 120,
      renderCell: (params) => (
        <RowActionsMenu
          id={params.row.id}
          rowData={params.row}
          onEdit={handleEdit}
        />
      ),
    },
  ];

  const RowActionsMenu = ({ id, rowData, onEdit }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { sendRequest } = useAxiosRequests();

    const handleRowOptionsClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleRowOptionsClose = () => {
      setAnchorEl(null);
    };

    const handleDelete = async () => {
      if (!window.confirm("Voulez-vous vraiment supprimer cette société ?")) return;
      try {
        await sendRequest({
          method: "DELETE",
          url: `${ExternalRoutes.society.delete}/${id}`,
        });
      } catch (err) {}
      handleRowOptionsClose();
    };

    const handleEditClick = () => {
      if (onEdit) onEdit(rowData);
      handleRowOptionsClose();
    };

    return (
      <>
        <IconButton size="small" onClick={handleRowOptionsClick}>
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleRowOptionsClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{ style: { minWidth: "8rem", zIndex: 1300 } }}
        >
          <MenuItem onClick={handleEditClick}>Modifier</MenuItem>
          {/* <MenuItem onClick={handleDelete}>Supprimer</MenuItem> */}
        </Menu>
      </>
    );
  };

  return (
    <>
      <Box
        className="kl-admin-header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          🏢 Liste des Sociétés
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="🔍 Rechercher..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 2 }}
      />
          <DataGrid
            disableColumnMenu
            pagination
            rows={companies.member}
            loading={loading}
            rowCount={companies.totalItems !== undefined ? companies.totalItems : 0}
            columns={columns}
            sortingMode="server"
            paginationMode="server"
            onSortModelChange={handleSortModelChange}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationChange}
            componentsProps={{
              pagination: {
                labelRowsPerPage: 'Lignes par page',
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from} Sur ${to} de ${count !== -1 ? count : `plus de ${to}`}`
              }
            }}
            localeText={{
              noRowsLabel: 'Aucune donnée disponible',
            }}
          />
      <CompanyEdit
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        company={selectedCompany}
        onCompanyUpdated={handleCompanyUpdated}
      />
    </>
  );
};

export default CompanyList;